import React, { useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Stack,
} from '@mui/material'
import Header from '../components/header'
import { pallets } from '../data/pallets'
import background from '../_contentnew/pulsebanner2.webp'

const categories = [
  'All',
  'UK Standard',
  'Euro',
  'CP',
  'Plastic',
  'Specialist',
  'Collars',
]

const PalletsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All')

  const filteredPallets = useMemo(() => {
    if (selectedCategory === 'All') return pallets

    const categoryMap = {
      'UK Standard': 'UK Standard Pallets',
      Euro: 'Euro Pallets',
      CP: 'CP Pallets',
      Plastic: 'Plastic Pallets',
      Specialist: 'Specialist Pallets',
      Collars: 'Pallet Collars',
    }

    return pallets.filter(
      pallet => pallet.category === categoryMap[selectedCategory]
    )
  }, [selectedCategory])

  return (
    <>
      <Header
        background={background}
        text="Pallets"
      />

      {/* TOP STRIP */}
      <Box
        sx={{
          backgroundColor: '#0A9B48',
          py: 3,
          px: 2,
        }}
      >
        <Typography
          sx={{
            color: 'white',
            textAlign: 'center',
            fontSize: {
              xs: '1.1rem',
              md: '1.4rem',
            },
            fontWeight: 600,
          }}
        >
          Browse our full range of pallet solutions for storage, transport and distribution.
        </Typography>
      </Box>
      

      {/* INTRO */}

      <Box
        sx={{
          py: 8,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            maxWidth: '900px',
            mx: 'auto',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: '#fff',
              fontWeight: 700,
              mb: 3,
            }}
          >
            Our Pallet Range
          </Typography>

          <Typography
            sx={{
              color: '#d7e3dd',
              lineHeight: 1.8,
              fontSize: '1.05rem',
              mb: 4,
            }}
          >
            Pulse Pallets supplies a wide range of pallet solutions
            for storage, transportation and distribution requirements.
            Whether you require standard UK pallets, Euro pallets,
            CP pallets or specialist pallet solutions, we can help.
          </Typography>

          <Typography
            sx={{
              color: '#57C84D',
              fontWeight: 600,
              fontStyle: 'italic',
            }}
          >
            All prices shown exclude VAT.
          </Typography>
        </Box>
      </Box>

      {/* FILTERS */}

      <Box
        sx={{
          width: '90%',
          maxWidth: '1200px',
          mx: 'auto',
          mb: 6,
          overflowX: 'auto',

          '&::-webkit-scrollbar': {
            height: '6px',
            color: 'white'
          },
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            gap: 2,
            minWidth: 'max-content',
            pb: 1,
          }}
        >
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              sx={{
                backgroundColor:
                  selectedCategory === category
                    ? '#0A9B48'
                    : '#222023',

                color: '#fff',

                border:
                  selectedCategory === category
                    ? '1px solid #0A9B48'
                    : '1px solid rgba(255,255,255,0.15)',

                px: 3,
                py: 1.5,
                borderRadius: 0,
                whiteSpace: 'nowrap',
                flexShrink: 0,

                '&:hover': {
                  backgroundColor:
                    selectedCategory === category
                      ? '#57C84D'
                      : '#2B2930',
                },
              }}
            >
              {category}
            </Button>
          ))}
        </Box>
      </Box>

      {/* PRODUCTS */}

      <Box
        sx={{
          width: '90%',
          maxWidth: '1200px',
          mx: 'auto',
          pb: 10,
        }}
      >
        <Grid
          container
          spacing={3}
          justifyContent="center"
        >
          {filteredPallets.map((pallet) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              key={pallet.id}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#222023',
                  border: '1px solid rgba(87,200,77,0.15)',
                  transition: 'all 0.3s ease',

                  '&:hover': {
                    borderColor: '#57C84D',
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                {/* IMAGE */}

                <Box
                  sx={{
                    backgroundColor: '#fff',
                    p: 2,
                    height: '240px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CardMedia
                    component="img"
                    image={pallet.image}
                    alt={pallet.name}
                    sx={{
                      maxHeight: '200px',
                      width: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>

                {/* CONTENT */}

                <CardContent
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                      mb: 2,
                    }}
                  >
                    {pallet.name}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#57C84D',
                      fontWeight: 600,
                      mb: 2,
                    }}
                  >
                    {pallet.dimensions}
                  </Typography>

                  {pallet.weight && (
                    <Typography
                      sx={{
                        color: '#d7e3dd',
                        mb: 1,
                      }}
                    >
                      Weight: {pallet.weight}
                    </Typography>
                  )}

                  {pallet.loadCapacity && (
                    <Typography
                      sx={{
                        color: '#d7e3dd',
                        mb: 3,
                      }}
                    >
                      Load Capacity: {pallet.loadCapacity}
                    </Typography>
                  )}

                  <Box sx={{ mt: 'auto' }}>
                    <Typography
                      sx={{
                        color: '#fff',
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        mb: 3,
                      }}
                    >
                      {pallet.price}
                    </Typography>

                    <Button
                      href="/contact"
                      sx={{
                        backgroundColor: '#0A9B48',
                        color: '#fff',
                        width: '100%',
                        py: 1.5,
                        borderRadius: 0,
                        fontWeight: 700,

                        '&:hover': {
                          transform: 'translateY(-5px)',
                          borderColor: 'white',
                          border: '1px solid',
                          boxShadow: '0 10px 30px rgba(87,200,77,0.15)',
                          backgroundColor: '#57C84D'
                        }
                      }}
                    >
                      ENQUIRE →
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  )
}

export default PalletsPage